import React, { useState, useEffect } from 'react';
import { BrickCharts, ChartSource } from 'brickcharts/dist/brickcharts.es.js';

function TrendAnalysis({ chartType = 'hot-100' }) {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentChart, setCurrentChart] = useState(null);
  const [previousChart, setPreviousChart] = useState(null);

  useEffect(() => {
    const analyzeTrends = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const brickCharts = new BrickCharts({
          enableCache: true
        });

        // Fetch current chart data
        const current = await brickCharts.getChart(chartType, ChartSource.BILLBOARD);
        setCurrentChart(current);

        // Try to get previous week's data for comparison
        let previous = null;
        try {
          // This would require historical data access
          // For now, we'll simulate trend analysis with current data
          previous = current;
        } catch (error) {
          console.warn('Could not fetch previous chart data:', error);
        }

        setPreviousChart(previous);

        // Analyze trends from current data
        const trendData = analyzeChartTrends(current, previous);
        setTrends(trendData);

      } catch (error) {
        setError(error.message);
        console.error('Trend analysis error:', error);
      } finally {
        setLoading(false);
      }
    };

    analyzeTrends();
  }, [chartType]);

  const analyzeChartTrends = (current, previous) => {
    if (!current || !current.entries) {
      return {
        newEntries: 0,
        droppedEntries: 0,
        totalEntries: 0,
        topMovers: { climbers: [], fallers: [] },
        chartType: chartType,
        date: current?.date || new Date()
      };
    }

    const entries = current.entries;
    const newEntries = entries.filter(entry => !entry.lastWeek).length;
    const totalEntries = entries.length;

    // Calculate top movers (simulated since we don't have previous data)
    const topMovers = {
      climbers: entries
        .filter(entry => entry.lastWeek && entry.rank < entry.lastWeek)
        .map(entry => ({
          entry,
          positionChange: entry.lastWeek - entry.rank
        }))
        .sort((a, b) => b.positionChange - a.positionChange)
        .slice(0, 10),
      fallers: entries
        .filter(entry => entry.lastWeek && entry.rank > entry.lastWeek)
        .map(entry => ({
          entry,
          positionChange: entry.rank - entry.lastWeek
        }))
        .sort((a, b) => b.positionChange - a.positionChange)
        .slice(0, 10)
    };

    // Calculate additional statistics
    const averagePosition = entries.reduce((sum, entry) => sum + entry.rank, 0) / entries.length;
    const medianPosition = entries[Math.floor(entries.length / 2)]?.rank || 0;
    
    const genreBreakdown = entries.reduce((acc, entry) => {
      // This would require genre data from the API
      // For now, we'll use a placeholder
      const genre = 'Unknown';
      acc[genre] = (acc[genre] || 0) + 1;
      return acc;
    }, {});

    return {
      newEntries,
      droppedEntries: 0, // Would need previous data to calculate
      totalEntries,
      topMovers,
      chartType: current.chartType || chartType,
      date: current.date || new Date(),
      averagePosition: Math.round(averagePosition),
      medianPosition,
      genreBreakdown,
      weekChange: {
        totalMovers: topMovers.climbers.length + topMovers.fallers.length,
        biggestClimber: topMovers.climbers[0] || null,
        biggestFaller: topMovers.fallers[0] || null
      }
    };
  };

  const getChangeSymbol = (current, last) => {
    if (!last) return 'NEW';
    if (current < last) return '↑';
    if (current > last) return '↓';
    return '→';
  };

  const getChangeClass = (current, last) => {
    if (!last) return 'new';
    if (current < last) return 'up';
    if (current > last) return 'down';
    return 'same';
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Analyzing chart trends...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <h3>Error Analyzing Trends</h3>
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="retry-btn"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!trends) {
    return (
      <div className="error">
        <p>No trend data available</p>
      </div>
    );
  }

  return (
    <div className="trend-analysis">
      <div className="trend-header">
        <h2>📈 Trend Analysis</h2>
        <p>Chart movement analysis for {trends.chartType.replace('-', ' ').toUpperCase()}</p>
        {trends.date && (
          <p className="analysis-date">
            Analysis date: {new Date(trends.date).toLocaleDateString()}
          </p>
        )}
      </div>

      <div className="trend-summary">
        <div className="trend-stat">
          <h3>📊 Chart Overview</h3>
          <div className="stat-grid">
            <div className="stat-item">
              <span className="stat-label">Total Entries</span>
              <span className="stat-value">{trends.totalEntries}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">New Entries</span>
              <span className="stat-value">{trends.newEntries}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Dropped Entries</span>
              <span className="stat-value">{trends.droppedEntries}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Average Position</span>
              <span className="stat-value">#{trends.averagePosition}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Median Position</span>
              <span className="stat-value">#{trends.medianPosition}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Movers</span>
              <span className="stat-value">{trends.weekChange.totalMovers}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="top-movers">
        <div className="climbers">
          <h3>🚀 Biggest Climbers</h3>
          {trends.topMovers.climbers.length > 0 ? (
            <div className="movers-list">
              {trends.topMovers.climbers.map((trend, index) => (
                <div key={index} className="trend-entry">
                  <div className="trend-rank">
                    <span className="current-rank">#{trend.entry.rank}</span>
                    <span className="position-change up">
                      +{trend.positionChange}
                    </span>
                  </div>
                  <div className="trend-info">
                    <span className="title">{trend.entry.title}</span>
                    <span className="artist">{trend.entry.artist}</span>
                  </div>
                  <div className="trend-details">
                    <span className="previous-rank">
                      from #{trend.entry.lastWeek}
                    </span>
                    {trend.entry.weeksOnChart && (
                      <span className="weeks">
                        {trend.entry.weeksOnChart} weeks
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No climbers this week</p>
          )}
        </div>

        <div className="fallers">
          <h3>📉 Biggest Fallers</h3>
          {trends.topMovers.fallers.length > 0 ? (
            <div className="movers-list">
              {trends.topMovers.fallers.map((trend, index) => (
                <div key={index} className="trend-entry">
                  <div className="trend-rank">
                    <span className="current-rank">#{trend.entry.rank}</span>
                    <span className="position-change down">
                      -{trend.positionChange}
                    </span>
                  </div>
                  <div className="trend-info">
                    <span className="title">{trend.entry.title}</span>
                    <span className="artist">{trend.entry.artist}</span>
                  </div>
                  <div className="trend-details">
                    <span className="previous-rank">
                      from #{trend.entry.lastWeek}
                    </span>
                    {trend.entry.weeksOnChart && (
                      <span className="weeks">
                        {trend.entry.weeksOnChart} weeks
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No fallers this week</p>
          )}
        </div>
      </div>

      {trends.weekChange.biggestClimber && (
        <div className="highlight-mover">
          <h3>🏆 Biggest Mover of the Week</h3>
          <div className="highlight-entry">
            <div className="highlight-rank">
              <span className="rank">#{trends.weekChange.biggestClimber.entry.rank}</span>
              <span className="change up">
                +{trends.weekChange.biggestClimber.positionChange}
              </span>
            </div>
            <div className="highlight-info">
              <span className="title">{trends.weekChange.biggestClimber.entry.title}</span>
              <span className="artist">{trends.weekChange.biggestClimber.entry.artist}</span>
            </div>
            <div className="highlight-details">
              <span>Moved from #{trends.weekChange.biggestClimber.entry.lastWeek}</span>
            </div>
          </div>
        </div>
      )}

      <div className="trend-insights">
        <h3>💡 Trend Insights</h3>
        <div className="insights-grid">
          <div className="insight">
            <h4>New Entries</h4>
            <p>
              {trends.newEntries} new entries entered the chart this week, 
              representing {((trends.newEntries / trends.totalEntries) * 100).toFixed(1)}% 
              of the total chart.
            </p>
          </div>
          <div className="insight">
            <h4>Chart Movement</h4>
            <p>
              {trends.weekChange.totalMovers} songs changed position this week, 
              with {trends.topMovers.climbers.length} climbing and {trends.topMovers.fallers.length} falling.
            </p>
          </div>
          <div className="insight">
            <h4>Average Position</h4>
            <p>
              The average position on the chart is #{trends.averagePosition}, 
              with a median position of #{trends.medianPosition}.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TrendAnalysis; 